import type { IssuedCredential, RevealedPass } from '@sentra/contracts';
import { formatPassCode, PASS_QR_OPTIONS, roleHasPermission } from '@sentra/contracts';
import { Eye, EyeOff } from 'lucide-react';
import encodeQR from 'qr';
import { type ReactNode, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { apiRoutes } from '@/api/routes';
import { useApiErrorMessage } from '@/api/use-api-error';
import { useIsPreprinted } from '@/app/providers/config';
import { Button } from '@/design/components/button';
import { useApiClient, useSession } from '@/features/auth/session';
import { PrinterCalibration } from '@/features/scan/calibrate';
import type { PrintSurface } from '@/features/scan/print';
import { framePrintSurface, PrintPassButton } from '@/features/scan/print';

/**
 * Delivering a pass to the person it belongs to, in whichever way this installation issues them.
 *
 * **The mode decides what this screen IS** (AD-59), and it is read from the configuration provider
 * rather than guessed. Under on-demand issuance the pass exists only as bytes until somebody puts
 * it somewhere, so the screen shows the symbol for the person to photograph and offers the A4
 * sheet. Under pre-printed issuance the person is already wearing the band: the screen confirms
 * WHICH band was bound to them and offers no print at all, because a paper copy would be a second
 * physical token for one credential — two things that open one door, one of which nobody can
 * revoke because nobody knows it exists.
 *
 * **The print action comes back for an `emergency_mint`**, which is the one credential in a
 * pre-printed installation with no band behind it: the box was empty, an administrator minted a
 * code, and the person still has to leave with something. That is not decided here, on EITHER
 * branch: both the reveal and the issue answer `printOffered`, computed from the installation's
 * mode and that credential's own origin, so the browser obeys one authority instead of re-deriving
 * the rule. The issued branch used to derive it, which was right for every pass except that one.
 *
 * **The code is not on screen until somebody asks.** A gate terminal is a shared screen in a public
 * place and a pass code is a bearer token: showing one by default would put a live credential in
 * front of whoever walks past, and in every screenshot anybody takes of this screen.
 *
 * **Revealing is a request, because it is an audited disclosure.** The code a registration just
 * produced is already in this browser's memory, so the symbol can be drawn from it immediately;
 * showing a code for a credential issued earlier has to go to the server, and the server writes the
 * row that says somebody looked. Both paths draw the same symbol, from one function.
 */

/** What is known about the pass being delivered, from whichever side it arrived. */
export type DeliverablePass =
  /** Straight out of a registration: the code is already here and needs no second disclosure. */
  | { readonly kind: 'issued'; readonly issued: IssuedCredential; readonly shortRef: string }
  /** A credential that already existed. The code is fetched, and fetching it is audited. */
  | { readonly kind: 'stored'; readonly credentialId: string; readonly shortRef: string };

/** The four things a delivered pass is made of, whichever way it arrived. */
interface Shown {
  readonly credentialId: string;
  /**
   * The canonical code, which is what the symbol encodes and what the grouped form is derived from.
   *
   * The server's `display` is deliberately NOT kept beside it: two renderings of one code are two
   * things to keep in step, and `formatPassCode` is the same function that produced the server's.
   */
  readonly code: string;
  readonly shortRef: string;
  readonly printOffered: boolean;
}

/**
 * What an issue just produced, with the print question answered by the server rather than here.
 *
 * **This used to derive `printOffered` as "not a pre-printed installation", and that was wrong for
 * exactly one credential.** `credentials/print.ts` offers the sheet when the mode is on-demand OR
 * the origin is `emergency_mint`, and the emergency mint is the one pass in a pre-printed
 * installation with no band behind it: the sheet is not an extra copy there, it is the only
 * physical token the holder will ever have. The derivation was safe only while an emergency mint
 * could not reach this branch, and it can — the record screen delivers whatever it has just issued
 * through it. `issuedCredentialSchema` now carries the flag, so both branches below read one
 * authority and neither guesses.
 */
function shownFromIssued(pass: Extract<DeliverablePass, { kind: 'issued' }>): Shown {
  return {
    credentialId: pass.issued.credential.id,
    code: pass.issued.code,
    shortRef: pass.shortRef,
    printOffered: pass.issued.printOffered,
  };
}

function shownFromRevealed(revealed: RevealedPass): Shown {
  return {
    credentialId: revealed.credential.id,
    code: revealed.code,
    shortRef: revealed.shortRef,
    printOffered: revealed.printOffered,
  };
}

export function PassDelivery({
  pass,
  personName,
  surface,
}: Readonly<{
  pass: DeliverablePass;
  /** The holder's first name, which the printed sheet carries and only this side can read. */
  personName: string;
  /** Injected, because `jsdom` has no printer. */
  surface?: PrintSurface;
}>): ReactNode {
  const { t } = useTranslation();
  const client = useApiClient();
  const session = useSession();
  const preprinted = useIsPreprinted();
  const describe = useApiErrorMessage();
  const [revealed, setRevealed] = useState<Shown | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const printSurface = useMemo(() => surface ?? framePrintSurface(document), [surface]);

  const reveal = useCallback(() => {
    setFailure(null);
    if (pass.kind === 'issued') {
      // Already disclosed, one moment ago, by the request that created it. A second `credential.
      // reveal` row here would record a disclosure that did not happen.
      setRevealed(shownFromIssued(pass));
      return;
    }
    setWorking(true);
    void (async () => {
      try {
        const answer = await client.request(apiRoutes.credentialReveal, {
          params: { id: pass.credentialId },
          body: { method: 'manual_search' },
        });
        setRevealed(shownFromRevealed(answer));
      } catch (error) {
        setFailure(describe(error));
      } finally {
        setWorking(false);
      }
    })();
  }, [client, describe, pass]);

  const canReveal =
    pass.kind === 'issued' ||
    (session !== null && roleHasPermission(session.user.role, 'credential:reveal'));

  const symbol = useMemo(
    () => (revealed === null ? null : encodeQR(revealed.code, 'svg', { ...PASS_QR_OPTIONS })),
    [revealed],
  );

  return (
    <section className="grid gap-3 rounded-lg border bg-card p-4">
      <h2 className="text-lg font-semibold">{t('scan.delivery.title', { name: personName })}</h2>

      {preprinted && (
        <div className="rounded-md border border-accent bg-accent-muted p-3 text-sm">
          <p className="font-medium">{t('scan.delivery.bound')}</p>
          <p className="text-muted-foreground">{t('scan.delivery.boundBody')}</p>
        </div>
      )}

      {revealed === null ? (
        <div className="grid gap-1">
          {/*
            A code already in hand needs no permission to show, because showing it discloses
            nothing the request that produced it did not. Fetching a STORED one does: it is a live
            bearer token on a screen, which is why `credential:reveal` is admin-and-above and why an
            operator is not offered a button the server would refuse.
          */}
          {canReveal && (
            <Button type="button" data-testid="reveal-pass" onClick={reveal} disabled={working}>
              <Eye aria-hidden />
              {t('scan.delivery.reveal')}
            </Button>
          )}
          {failure !== null && (
            <p role="alert" className="text-sm text-danger">
              {failure}
            </p>
          )}
        </div>
      ) : (
        <div className="grid gap-3">
          <p className="text-sm text-muted-foreground">{t('scan.delivery.revealBody')}</p>
          <div
            className="w-48 self-center bg-white p-2"
            role="img"
            aria-label={t('scan.delivery.symbol')}
            // The symbol is produced by this application from a code it already holds, never from
            // anything a request carried: `encodeQR` emits its own SVG and nothing interpolates.
            dangerouslySetInnerHTML={{ __html: symbol ?? '' }}
          />
          <dl className="grid gap-1 text-sm">
            <dt className="text-muted-foreground">{t('scan.delivery.code')}</dt>
            <dd data-testid="pass-code" className="font-mono text-lg tracking-widest">
              {formatPassCode(revealed.code)}
            </dd>
            <dt className="text-muted-foreground">{t('scan.delivery.shortRef')}</dt>
            <dd>{revealed.shortRef}</dd>
          </dl>

          {revealed.printOffered ? (
            <div className="grid gap-3">
              <PrintPassButton
                credentialId={revealed.credentialId}
                firstName={personName}
                // AD-53: how the action was reached. A pass just issued was reached by the
                // registration the operator was keying; a stored one was reached by search.
                method={pass.kind === 'issued' ? 'manual_key' : 'manual_search'}
                surface={printSurface}
              />
              <PrinterCalibration surface={printSurface} />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">{t('scan.delivery.noPrint')}</p>
          )}

          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setRevealed(null);
            }}
          >
            <EyeOff aria-hidden />
            {t('scan.delivery.hide')}
          </Button>
        </div>
      )}
    </section>
  );
}
